import { EChartTimeseries } from "../../../components/EChartTimeseries.js";
import { useMemoizedBarSeries } from "../../../hooks/chart.js";
import { type BasicUsageChartProps, UsageChartWrapper } from "./UsageChartWrapper.js";
import { IconDatabase } from "@tabler/icons-react";
import { checkIsApproachingLimit } from "./utils/limits.js";
import { TIERED_PRODUCTS, TieredPriceProductId } from "@project/common";
import { useStorageUsage } from "./hooks/fetchUsages.js";
import { resolveUsageEventDeltas } from "./utils/usageEventsProcessors.js";
import { useProductNames } from "./hooks/productLabels.js";
import { useStorageUsageSum } from "./hooks/fetchSums.js";
import { FormattedMessage, useIntl } from "react-intl";
import { Text } from "@mantine/core";

export const StorageUsageChart = ({ from, to, interval, addToDetailedView }: BasicUsageChartProps) => {
  const {
    storageUsage,
    isLoadingStorage: isLoadingStorageHist,
    isErrorStorage: isErrorStorageHist,
  } = useStorageUsage({ from, to, interval });

  const {
    usageSum,
    isLoadingStorage: isLoadingStorageSum,
    isErrorStorage: isErrorStorageSum,
  } = useStorageUsageSum({ to });

  const intl = useIntl();
  const getName = useProductNames();
  const title = getName(TieredPriceProductId.TieredStorage);

  const series = useMemoizedBarSeries(
    resolveUsageEventDeltas(storageUsage, usageSum),
    title,
    TIERED_PRODUCTS[TieredPriceProductId.TieredStorage].freeTierAllowance,
    intl.formatMessage({ defaultMessage: "1GB free", id: "allowance.storage" }),
  );

  return (
    <UsageChartWrapper
      title={title}
      iconColor="pink.1"
      hasUsage={storageUsage.length > 0}
      icon={<IconDatabase size={20} />}
      addToDetailedView={addToDetailedView}
      hasError={isErrorStorageHist || isErrorStorageSum}
      isLoading={isLoadingStorageHist || isLoadingStorageSum}
      isApproachingLimit={checkIsApproachingLimit(storageUsage, TieredPriceProductId.TieredStorage)}
    >
      <EChartTimeseries series={series} />
      <Text mt="sm">
        <FormattedMessage
          defaultMessage="Active storage: {usageSum}"
          values={{ usageSum }}
          id="storage.sum"
        />
      </Text>
    </UsageChartWrapper>
  );
};
